import { useState } from 'react';
import { useRegistrationStore } from '../../store/registrationStore';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../Button';

const AGE_GROUP_LABELS: Record<string, string> = {
  adult: 'Adult (18+)',
  teen: 'Teen (13-17)',
  youth: 'Youth (6-12)',
  child: 'Child (3-5)',
  infant: 'Infant/Toddler (2 and under)',
};

const VILLA_TYPE_LABELS: Record<string, string> = {
  'standard-2bed': '2 rooms',
  'standard-3bed': '3 rooms',
  'host-villa': '4+ rooms',
};

interface StepReviewSubmitProps {
  onBack: () => void;
  onSubmit: () => Promise<void>;
}

export function StepReviewSubmit({ onBack, onSubmit }: StepReviewSubmitProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const user = useAuthStore((s) => s.user);
  const { groupLeaderData, familyMembers, villaPreference, specialNeeds } =
    useRegistrationStore();

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit();
    } catch (error: any) {
      setSubmitError(error.message || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-slate-900">Review & Submit</h2>
      <p className="text-sm text-slate-600">
        Please review your registration details before submitting.
      </p>

      {/* Section: Group Leader */}
      <div className="border border-slate-200 rounded-lg p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-2">Group Leader</h3>
        {user && (
          <p className="text-sm text-slate-600">
            {user.firstName} {user.lastName} ({user.email})
          </p>
        )}
        <p className="text-sm text-slate-600">Phone: {groupLeaderData.phone || 'Not provided'}</p>
        <p className="text-sm text-slate-600">
          Role: {groupLeaderData.isGroupLeader ? 'Group Leader' : 'Individual'}
        </p>
        {groupLeaderData.familyConnection && (
          <p className="text-xs text-slate-500 mt-2">
            <span className="font-medium text-slate-600">Family connection:</span>{' '}
            {groupLeaderData.familyConnection}
          </p>
        )}
      </div>

      {/* Section: Family Members */}
      <div className="border border-slate-200 rounded-lg p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-2">
          Family Members ({familyMembers.length})
        </h3>
        {familyMembers.length === 0 && (
          <p className="text-sm text-slate-400 italic">No family members added.</p>
        )}
        {familyMembers.map((member, i) => {
          const needs = specialNeeds.memberNeeds[i];
          return (
            <div key={i} className="py-2 border-b border-slate-100 last:border-0">
              <p className="text-sm text-slate-700 font-medium">
                {member.firstName} {member.lastName}
              </p>
              <p className="text-xs text-slate-500">
                {AGE_GROUP_LABELS[member.ageGroup] || member.ageGroup} | {member.relationship}
              </p>
              {needs?.dietaryRestrictions?.length > 0 && (
                <p className="text-xs text-slate-500">
                  Dietary: {needs.dietaryRestrictions.join(', ')}
                </p>
              )}
              {needs?.foodAllergies && (
                <p className="text-xs text-amber-600">
                  Allergies: {needs.foodAllergies}
                </p>
              )}
              {needs?.accessibilityNeeds && (
                <p className="text-xs text-slate-500">
                  Accessibility: {needs.accessibilityNeeds}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Section: Room Accommodations */}
      <div className="border border-slate-200 rounded-lg p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-2">Room Accommodations</h3>
        <p className="text-sm text-slate-600">
          {villaPreference.villaTypePreference
            ? VILLA_TYPE_LABELS[villaPreference.villaTypePreference] ||
              villaPreference.villaTypePreference
            : 'No preference selected'}
        </p>
        {villaPreference.flexibilityNotes && (
          <p className="text-xs text-slate-500 mt-1">
            Flexibility: {villaPreference.flexibilityNotes}
          </p>
        )}
        {villaPreference.roomConfigNotes && (
          <p className="text-xs text-slate-500 mt-1">
            Room config: {villaPreference.roomConfigNotes}
          </p>
        )}
      </div>

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {submitError}
        </div>
      )}

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Registration'}
        </Button>
      </div>
    </div>
  );
}
